import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [ids, setIds] = useState(() => new Set());
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (!user || user === false) {
            setItems([]);
            setIds(new Set());
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.get("/me/favorites");
            setItems(data);
            setIds(new Set(data.map((t) => t.id)));
        } catch {
            setItems([]);
            setIds(new Set());
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const isFavorite = useCallback((id) => ids.has(id), [ids]);

    const toggle = useCallback(
        async (track) => {
            if (!user || user === false) {
                toast.info("Connecte-toi pour sauvegarder tes favoris", { duration: 2500 });
                return { ok: false, requiresAuth: true };
            }
            const wasFav = ids.has(track.id);
            // Optimistic update
            const nextIds = new Set(ids);
            if (wasFav) nextIds.delete(track.id);
            else nextIds.add(track.id);
            setIds(nextIds);
            setItems((prev) => (wasFav ? prev.filter((t) => t.id !== track.id) : [track, ...prev]));
            try {
                const { data } = await api.post(`/me/favorites/${track.id}`);
                if (data.favorited) toast.success("Ajouté à tes favoris");
                else toast("Retiré de tes favoris");
                return { ok: true, favorited: data.favorited };
            } catch (e) {
                // Revert on error
                setIds(ids);
                setItems((prev) => (wasFav ? [track, ...prev] : prev.filter((t) => t.id !== track.id)));
                toast.error("Impossible de mettre à jour le favori");
                return { ok: false };
            }
        },
        [ids, user]
    );

    const value = useMemo(
        () => ({ items, ids, loading, isFavorite, toggle, refresh, count: ids.size }),
        [items, ids, loading, isFavorite, toggle, refresh]
    );

    return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export const useFavorites = () => useContext(FavoritesContext);
